import {
  BadgeCheck,
  Building2,
  Globe,
  User,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Code,
  FileSearch,
  Lock,
  Eye,
} from 'lucide-react';
import ScoreCard from './ScoreCard';
import { Badge } from '@/components/ui/Badge';
import { Separator } from '@/components/ui/Separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';

// Full VendorGuard Intelligence interface
interface VendorGuardIntelligence {
  identity?: {
    username: string;
    platforms_found: string[];
    platform_count: number;
    cross_platform_consistency: 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN';
    gravatar_exists: boolean;
    identity_vintage_years: number;
    openpgp_key_found: boolean;
    openpgp_key_id: string | null;
  };
  breach_exposure?: {
    breach_count: number;
    oldest_breach_year: number | null;
    vintage_confirmed: boolean;
    is_ghost_profile: boolean;
  };
  security_hygiene?: {
    openssf_score: number | null;
    binary_artifacts_clean: boolean;
    branch_protection: boolean;
    pinned_dependencies: boolean;
    signed_commits: boolean;
    vulnerabilities_in_deps: number;
  };
  code_forensics?: {
    style_consistency: number;
    account_takeover_risk: boolean;
    malicious_patterns: string[];
    high_entropy_strings: boolean;
    hardcoded_ips: boolean;
    dangerous_calls: string[];
  };
  behavioral?: {
    timezone_consistent: boolean;
    claimed_location: string | null;
    inferred_timezone: string | null;
    is_abandonware: boolean;
    last_commit_days: number;
    open_critical_issues: number;
    bus_factor: number;
  };
  affiliation?: {
    corporate_domain_match: boolean;
    linkedin_verified: boolean;
    bio_entropy: 'HUMAN' | 'BOT_SUSPECTED' | 'UNKNOWN';
    employer_verified: string | null;
  };
  country_of_origin?: {
    inferred_country: string | null;
    country_code: string | null;
    confidence: 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN';
    signals: string[];
    timezone_country: string | null;
    location_country: string | null;
    email_domain_country: string | null;
    company_hq_country: string | null;
  };
  module_scores?: Record<string, { score: number; weight: number; status: string; summary: string }>;
  github_token_present?: boolean;
  trust_score: number;
  risk_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'TRUSTED';
  risk_factors: string[];
  trust_boosters: string[];
  summary: string;
}

interface CreatorInfo {
  name: string;
  organization?: string;
  verified: boolean;
  country?: string;
  organizationType?: string;
  trustScore: number;
  repoUrl?: string;
  vendorGuard?: VendorGuardIntelligence;
}

interface CreatorVerificationProps {
  creator: CreatorInfo;
}

const StatusIcon = ({ status, size = 'w-4 h-4' }: { status: boolean | null; size?: string }) => {
  if (status === true) return <CheckCircle className={`${size} text-green-500`} />;
  if (status === false) return <XCircle className={`${size} text-red-500`} />;
  return <AlertTriangle className={`${size} text-yellow-500`} />;
};

const ConfidenceBadge = ({ confidence }: { confidence: string }) => {
  const colors: Record<string, string> = {
    HIGH: 'bg-green-500/20 text-green-400 border-green-500/30',
    MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    LOW: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    UNKNOWN: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return (
    <Badge variant="outline" className={colors[confidence] || colors.UNKNOWN}>
      {confidence}
    </Badge>
  );
};

const DataRow = ({
  label,
  value,
  status,
}: {
  label: string;
  value: string | number | null;
  status?: boolean | null;
}) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      {status !== undefined && <StatusIcon status={status} />}
      <span className="font-medium">{value ?? 'N/A'}</span>
    </div>
  </div>
);

const DataRowLink = ({
  label,
  value,
  href,
  status,
}: {
  label: string;
  value: string | number | null;
  href?: string;
  status?: boolean | null;
}) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-muted-foreground">{label}</span>
    <div className="flex items-center gap-2">
      {status !== undefined && <StatusIcon status={status} />}
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
        >
          {value ?? 'N/A'}
        </a>
      ) : (
        <span className="font-medium">{value ?? 'N/A'}</span>
      )}
    </div>
  </div>
);

const platformUrl = (platform: string, username: string): string | null => {
  const urls: Record<string, string> = {
    GitHub: `https://github.com/${username}`,
    npm: `https://www.npmjs.com/~${username}`,
    PyPI: `https://pypi.org/user/${username}/`,
    'crates.io': `https://crates.io/users/${username}`,
    GitLab: `https://gitlab.com/${username}`,
    Bitbucket: `https://bitbucket.org/${username}/`,
    'Stack Overflow': `https://stackoverflow.com/users?q=${username}`,
    Twitter: `https://twitter.com/${username}`,
    Keybase: `https://keybase.io/${username}`,
    Hackage: `https://hackage.haskell.org/user/${username}`,
    Docker: `https://hub.docker.com/u/${username}`,
  };
  return urls[platform] || null;
};

/**
 * Normalize trust score to 0-10 scale
 * API may return 0-100 or 0-10, this ensures consistent display
 */
function normalizeTrustScore(score: number): number {
  if (score > 10) {
    // Score is on 0-100 scale, convert to 0-10
    return score / 10;
  }
  return score;
}

/**
 * Get the raw score (0-100) for percentage displays
 */
function getTrustScorePercent(score: number): number {
  if (score <= 10) {
    // Score is on 0-10 scale, convert to 0-100
    return score * 10;
  }
  return score;
}

const CreatorVerification = ({ creator }: CreatorVerificationProps) => {
  // Normalize the trust score - handle both 0-100 and 0-10 scales
  const normalizedScore = normalizeTrustScore(creator.trustScore);
  const scorePercent = getTrustScorePercent(creator.trustScore);
  const repoUrl = creator.repoUrl;

  const getRiskLevel = (score: number) => {
    // score here should be 0-100 for risk calculation
    if (score >= 80) return 'low';
    if (score >= 60) return 'medium';
    if (score >= 40) return 'high';
    return 'critical';
  };

  const vg = creator.vendorGuard;
  const trustScore = vg?.trust_score ?? (vg as Record<string, unknown> | undefined)?.trustScore as number ?? 0;

  // If no VendorGuard data, show simplified view
  if (!vg) {
    return (
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-yellow-400">Limited verification — no GitHub token</p>
            <p className="text-muted-foreground mt-1">
              VendorGuard requires a GitHub token to fetch creator profiles, commit history, and
              security metadata. Connect your GitHub account in Settings to enable full
              verification.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div
              className={`flex items-center gap-3 p-4 rounded-lg ${
                creator.verified
                  ? 'bg-risk-low-bg border border-risk-low/30'
                  : 'bg-risk-high-bg border border-risk-high/30'
              }`}
            >
              <BadgeCheck
                className={`w-8 h-8 ${creator.verified ? 'text-risk-low' : 'text-risk-high'}`}
              />
              <div>
                <p className="font-semibold">
                  {creator.verified ? 'Verified Creator' : 'Unverified Creator'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {creator.verified
                    ? 'Identity confirmed through official channels'
                    : 'Creator identity could not be verified'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <User className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Author</p>
                  <p className="font-medium">{creator.name}</p>
                </div>
              </div>

              {creator.organization && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Organization
                    </p>
                    <p className="font-medium">{creator.organization}</p>
                  </div>
                </div>
              )}

              {creator.country && (
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">
                      Country of Origin
                    </p>
                    <p className="font-medium">{creator.country}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <ScoreCard
              title="Creator Trust Score"
              score={normalizedScore}
              maxScore={10}
              riskLevel={getRiskLevel(scorePercent)}
              size="lg"
            />
          </div>
        </div>
      </div>
    );
  }

  // Full VendorGuard display
  const riskLevelColors: Record<string, string> = {
    TRUSTED: 'bg-green-500/20 text-green-400 border-green-500/50',
    LOW: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    MEDIUM: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
    HIGH: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
    CRITICAL: 'bg-red-500/20 text-red-400 border-red-500/50',
  };

  return (
    <div className="card-elevated p-6 animate-slide-up space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">7-Module Deep Scan Analysis</p>
        <Badge variant="outline" className={riskLevelColors[vg.risk_level]}>
          {vg.risk_level} RISK
        </Badge>
      </div>

      {/* Trust Score Banner */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4">
        <div className="flex items-center gap-6">
          <div className="flex-shrink-0 relative w-20 h-20">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-muted/30"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeDasharray={`${trustScore}, 100`}
                className={
                  trustScore >= 70
                    ? 'text-green-500'
                    : trustScore >= 40
                      ? 'text-yellow-500'
                      : 'text-red-500'
                }
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-lg font-bold">{trustScore}</span>
              <span className="text-[10px] text-muted-foreground">/100</span>
            </div>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Vendor Trust Score</p>
            <Badge
              variant="outline"
              className={
                trustScore >= 70
                  ? 'bg-green-500/20 text-green-400 border-green-500/30'
                  : trustScore >= 40
                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
              }
            >
              {trustScore >= 70 ? 'LOW RISK' : trustScore >= 40 ? 'MEDIUM RISK' : 'HIGH RISK'}
            </Badge>
          </div>
        </div>
      </div>

      {/* GitHub Token Warning */}
      {vg.github_token_present === false && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-yellow-400">Limited verification — no GitHub token</p>
            <p className="text-muted-foreground mt-1">
              VendorGuard requires a GitHub token to fetch creator profiles, commit history, and
              security metadata. The scores below are defaults and do not reflect the actual
              creator. Connect your GitHub account in Settings to enable full verification.
            </p>
          </div>
        </div>
      )}

      {/* Module Score Breakdown */}
      {vg.module_scores && Object.keys(vg.module_scores).length > 0 && (
        <div className="border rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            Module Score Breakdown
          </h4>
          <div className="space-y-2">
            {Object.entries(vg.module_scores).map(([key, mod]) => {
              const weightedScore = (mod.score * mod.weight) / 100;
              const barColor =
                mod.score >= 70
                  ? 'bg-green-500'
                  : mod.score >= 40
                    ? 'bg-yellow-500'
                    : 'bg-red-500';
              const moduleNames: Record<string, string> = {
                identity: '1. Identity Resolution',
                breach: '2. Breach & Exposure',
                security: '3. Security Hygiene',
                forensics: '4. Code Forensics',
                behavioral: '5. Behavioral Analysis',
                affiliation: '6. Affiliation',
                origin: '7. Country of Origin',
              };
              return (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{moduleNames[key] || key}</span>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-muted-foreground">
                        weight: {mod.weight}%
                      </span>
                      <span className="font-medium w-16 text-right">
                        {mod.score}/100
                      </span>
                      <span className="text-muted-foreground w-20 text-right">
                        contrib: {weightedScore.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor}`}
                      style={{ width: `${mod.score}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between items-center pt-2 border-t text-sm">
            <span className="font-medium">Weighted Total</span>
            <span className="font-bold">
              {Object.values(vg.module_scores)
                .reduce((sum, mod) => sum + (mod.score * mod.weight) / 100, 0)
                .toFixed(1)}
              /100
            </span>
          </div>
        </div>
      )}

      {/* 7 Modules Grid */}
      <Accordion type="multiple" defaultValue={['module1', 'module7']} className="space-y-2">
        {/* Module 1: Identity Resolution */}
        {vg.identity && (
          <AccordionItem value="module1" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-blue-400" />
                <span>Module 1: Identity Resolution</span>
                <Badge variant="secondary" className="ml-2">
                  {vg.identity.platform_count} platforms
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRowLink
                label="Username"
                value={vg.identity.username}
                href={`https://github.com/${vg.identity.username}`}
              />
              <DataRow
                label="Account Age"
                value={`${vg.identity.identity_vintage_years} years`}
                status={vg.identity.identity_vintage_years >= 2}
              />
              <DataRowLink
                label="Gravatar"
                value={vg.identity.gravatar_exists ? 'Found' : 'Not found'}
                href={vg.identity.gravatar_exists ? `https://gravatar.com/${vg.identity.username}` : undefined}
                status={vg.identity.gravatar_exists}
              />
              <DataRowLink
                label="OpenPGP Key"
                value={
                  vg.identity.openpgp_key_found
                    ? vg.identity.openpgp_key_id || 'Verified'
                    : 'Not found'
                }
                href={
                  vg.identity.openpgp_key_found && vg.identity.openpgp_key_id
                    ? `https://keys.openpgp.org/search?q=${vg.identity.openpgp_key_id}`
                    : undefined
                }
                status={vg.identity.openpgp_key_found}
              />
              <DataRow
                label="Cross-Platform"
                value={vg.identity.cross_platform_consistency}
                status={vg.identity.cross_platform_consistency === 'HIGH'}
              />
              <Separator className="my-2" />
              <div>
                <p className="text-xs text-muted-foreground mb-2">
                  Platforms Found ({vg.identity.platform_count}):
                </p>
                <div className="flex flex-wrap gap-1">
                  {vg.identity.platforms_found.map((p, i) => {
                    const url = platformUrl(p, vg.identity!.username);
                    return url ? (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold transition-colors border-blue-500/40 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300"
                      >
                        {p}
                      </a>
                    ) : (
                      <Badge key={i} variant="outline" className="text-xs">
                        {p}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 2: Breach & Exposure */}
        {vg.breach_exposure && (
          <AccordionItem value="module2" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-purple-400" />
                <span>Module 2: Breach & Exposure History</span>
                <StatusIcon status={!vg.breach_exposure.is_ghost_profile} />
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRow
                label="Breach Count"
                value={vg.breach_exposure.breach_count || 'None found'}
              />
              <DataRow
                label="Oldest Breach Year"
                value={vg.breach_exposure.oldest_breach_year || 'N/A'}
              />
              <DataRow
                label="Vintage Confirmed"
                value={vg.breach_exposure.vintage_confirmed ? 'Yes (>5 years)' : 'Unknown'}
                status={vg.breach_exposure.vintage_confirmed}
              />
              <DataRow
                label="Ghost Profile"
                value={vg.breach_exposure.is_ghost_profile ? 'Yes (Risk)' : 'No'}
                status={!vg.breach_exposure.is_ghost_profile}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 3: OpenSSF Security */}
        {vg.security_hygiene && (
          <AccordionItem value="module3" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-400" />
                <span>Module 3: OpenSSF Security Hygiene</span>
                {vg.security_hygiene.openssf_score && (
                  <Badge variant="secondary">
                    {vg.security_hygiene.openssf_score.toFixed(1)}/10
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRowLink
                label="OpenSSF Score"
                value={vg.security_hygiene.openssf_score?.toFixed(1) || 'Not available'}
                href={
                  repoUrl
                    ? `https://securityscorecards.dev/viewer/?uri=${repoUrl.replace(/^https?:\/\//, '')}`
                    : undefined
                }
                status={
                  vg.security_hygiene.openssf_score ? vg.security_hygiene.openssf_score > 5 : null
                }
              />
              <DataRow
                label="Binary Artifacts"
                value={vg.security_hygiene.binary_artifacts_clean ? 'Clean' : 'DETECTED'}
                status={vg.security_hygiene.binary_artifacts_clean}
              />
              <DataRow
                label="Branch Protection"
                value={vg.security_hygiene.branch_protection ? 'Enabled' : 'Not enabled'}
                status={vg.security_hygiene.branch_protection}
              />
              <DataRow
                label="Signed Commits"
                value={vg.security_hygiene.signed_commits ? 'Yes' : 'No'}
                status={vg.security_hygiene.signed_commits}
              />
              <DataRow
                label="Pinned Dependencies"
                value={vg.security_hygiene.pinned_dependencies ? 'Yes' : 'No'}
                status={vg.security_hygiene.pinned_dependencies}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 4: Code Forensics */}
        {vg.code_forensics && (
          <AccordionItem value="module4" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Code className="w-4 h-4 text-cyan-400" />
                <span>Module 4: Code Forensics & Stylometry</span>
                <StatusIcon
                  status={
                    !vg.code_forensics.account_takeover_risk &&
                    !vg.code_forensics.high_entropy_strings
                  }
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRow
                label="Style Consistency"
                value={`${vg.code_forensics.style_consistency}%`}
                status={vg.code_forensics.style_consistency > 70}
              />
              <DataRow
                label="Takeover Risk"
                value={vg.code_forensics.account_takeover_risk ? 'DETECTED' : 'None'}
                status={!vg.code_forensics.account_takeover_risk}
              />
              <DataRow
                label="High Entropy Strings"
                value={vg.code_forensics.high_entropy_strings ? 'Found (obfuscation?)' : 'Clear'}
                status={!vg.code_forensics.high_entropy_strings}
              />
              <DataRow
                label="Hardcoded IPs"
                value={vg.code_forensics.hardcoded_ips ? 'Found (C2 risk?)' : 'Clear'}
                status={!vg.code_forensics.hardcoded_ips}
              />
              {vg.code_forensics.dangerous_calls.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Dangerous Calls:</p>
                  <div className="flex flex-wrap gap-1">
                    {vg.code_forensics.dangerous_calls.map((c, i) => (
                      <Badge key={i} variant="destructive" className="text-xs">
                        {c}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 5: Behavioral Analysis */}
        {vg.behavioral && (
          <AccordionItem value="module5" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-orange-400" />
                <span>Module 5: Behavioral Analysis</span>
                <StatusIcon
                  status={vg.behavioral.timezone_consistent && !vg.behavioral.is_abandonware}
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRow
                label="Claimed Location"
                value={vg.behavioral.claimed_location || 'Not provided'}
              />
              <DataRow
                label="Inferred Timezone"
                value={vg.behavioral.inferred_timezone || 'Unknown'}
              />
              <DataRow
                label="Timezone Consistent"
                value={vg.behavioral.timezone_consistent ? 'Yes' : 'Mismatch'}
                status={vg.behavioral.timezone_consistent}
              />
              <DataRowLink
                label="Last Commit"
                value={`${vg.behavioral.last_commit_days} days ago`}
                href={repoUrl ? `${repoUrl}/commits` : undefined}
                status={vg.behavioral.last_commit_days < 180}
              />
              <DataRow
                label="Abandonware"
                value={vg.behavioral.is_abandonware ? 'YES' : 'No'}
                status={!vg.behavioral.is_abandonware}
              />
              <DataRow
                label="Bus Factor"
                value={vg.behavioral.bus_factor}
                status={vg.behavioral.bus_factor > 1}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 6: Affiliation */}
        {vg.affiliation && (
          <AccordionItem value="module6" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-indigo-400" />
                <span>Module 6: Affiliation Verification</span>
                <StatusIcon
                  status={vg.affiliation.corporate_domain_match || vg.affiliation.linkedin_verified}
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRow
                label="Corporate Domain Match"
                value={vg.affiliation.corporate_domain_match ? 'Verified' : 'Not verified'}
                status={vg.affiliation.corporate_domain_match}
              />
              <DataRow
                label="LinkedIn Verified"
                value={vg.affiliation.linkedin_verified ? 'Yes' : 'No'}
                status={vg.affiliation.linkedin_verified}
              />
              <DataRow
                label="Employer"
                value={vg.affiliation.employer_verified || 'Not verified'}
              />
              <DataRow
                label="Bio Analysis"
                value={vg.affiliation.bio_entropy}
                status={vg.affiliation.bio_entropy === 'HUMAN'}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Module 7: Country of Origin */}
        {vg.country_of_origin && (
          <AccordionItem value="module7" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-teal-400" />
                <span>Module 7: Country of Origin</span>
                {vg.country_of_origin.inferred_country && (
                  <Badge variant="secondary">{vg.country_of_origin.inferred_country}</Badge>
                )}
                <ConfidenceBadge confidence={vg.country_of_origin.confidence} />
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-3 pt-2">
              <DataRow
                label="Inferred Country"
                value={vg.country_of_origin.inferred_country || 'Unknown'}
              />
              <DataRow label="Country Code" value={vg.country_of_origin.country_code || 'N/A'} />
              <DataRow label="Confidence" value={vg.country_of_origin.confidence} />
              <Separator className="my-2" />
              <p className="text-xs text-muted-foreground">Signal Sources:</p>
              <DataRow
                label="Location Signal"
                value={vg.country_of_origin.location_country || 'N/A'}
              />
              <DataRow
                label="Timezone Signal"
                value={vg.country_of_origin.timezone_country || 'N/A'}
              />
              <DataRow
                label="Email Domain"
                value={vg.country_of_origin.email_domain_country || 'N/A'}
              />
              <DataRow
                label="Company HQ"
                value={vg.country_of_origin.company_hq_country || 'N/A'}
              />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Trust Boosters */}
      {(vg.trust_boosters?.length ?? 0) > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            Trust Boosters
          </h4>
          <div className="space-y-1">
            {vg.trust_boosters.map((booster, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 rounded px-3 py-1.5"
              >
                <CheckCircle className="w-3 h-3" />
                {booster}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {(vg.risk_factors?.length ?? 0) > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            Risk Factors
          </h4>
          <div className="space-y-1">
            {vg.risk_factors.map((risk, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 text-sm rounded px-3 py-1.5 ${
                  risk.includes('CRITICAL')
                    ? 'text-red-400 bg-red-500/10'
                    : 'text-orange-400 bg-orange-500/10'
                }`}
              >
                <AlertTriangle className="w-3 h-3" />
                {risk}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Executive Summary */}
      {vg.summary && (
        <div className="bg-muted/30 rounded-lg p-4 space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <FileSearch className="w-4 h-4" />
            Executive Summary
          </h4>
          <p className="text-sm text-muted-foreground leading-relaxed">{vg.summary}</p>
        </div>
      )}

      {/* Methodology Footer */}
      <div className="text-xs text-muted-foreground text-center pt-2 border-t">
        Methodology: OpenSSF + OSINT + NIST SP 800-161 | Data Sources: GitHub API, OpenSSF
        Scorecard, 30+ Platform Checks
      </div>
    </div>
  );
};

export default CreatorVerification;
